import useFormState, { createFormValidation } from 'use-form-state';
import { useI18n } from '~/features/i18n';

type QuotationFormValues = {
    variantId: number | null;
    languageId: number | null;
    /** `'custom'` (provider travels to you), a venue id, or null when nothing is chosen. */
    location: 'custom' | number | null;
};

const useQuotationCardFormState = (
    initialValues: Partial<QuotationFormValues>,
    /**
     * True when the activity genuinely offers a choice of location. With a single possible
     * answer the card fills it in itself, so demanding a selection would block a booking on a
     * field the user cannot meaningfully act on.
     */
    isLocationRequired = false,
) => {
    const i18n = useI18n();
    const validation = createFormValidation<QuotationFormValues>([
        {
            path: 'variantId',
            validate: value => typeof value === 'number',
            message: i18n._('Variant is required'),
        },
        {
            path: 'languageId',
            validate: value => typeof value === 'number',
            message: i18n._('Language is required'),
        },
        {
            path: 'location',
            validate: value => !isLocationRequired || value != null,
            message: i18n._('quotation.location.required', undefined, {
                message: 'Location is required',
            }),
        },
    ]);

    return useFormState<QuotationFormValues, QuotationFormValues>(
        {
            variantId: null,
            languageId: null,
            location: null,
            ...initialValues,
        },
        {
            validation,
            valuesToInput: values => values,
        },
    );
};

export default useQuotationCardFormState;
